import { Component, ViewChild, input, output } from '@angular/core';
import { Observable } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { AppMatAutocompleteComponent, XmedicalUiModule } from '@xmedical/ui';
import { Medication, MedicationOverlapKind, MedicineLibraryProductMatch, PipesModule } from '@xmedical/core';
import { WithReferralInfo } from '../../../types/referral-info';
import { MedicationListViewComponent } from '../medication-list-view.component';
import { MedicationSearchEntry } from './medication-search-entry';
import { MedicineLibraryProductResultComponent } from '../medicine-library-product-result/medicine-library-product-result.component';

/**
 * Generic interface for displayed medication items.
 */
export interface MedicationDisplayItem extends WithReferralInfo {
  id?: number;
  description: string;
  dosage?: string;
  diagnosisCode?: string;
  comment?: string;
  lastModifiedDate?: Date;
  modifiedDateUtc?: Date;
  lastModifiedUser?: { firstName?: string; surname?: string };
  // The server's overlap flag and reason (#333, v2 patient list). When missing, the list view falls back to
  // its own text and type checks below.
  overlap?: MedicationOverlapKind;
  overlapReason?: string;
  // For duplicate type detection when there is no server flag
  typeId?: number;
  typeUnassigned?: boolean;
  // Optional text color
  textColour?: string;
}

/**
 * Event emitted when a medication is added from search results.
 */
export interface MedicationAddedEvent {
  item: Medication;
  dosage: string;
  diagnosisCode: string;
  comment: string;
}

/**
 * Event emitted when a medication is removed from the list.
 */
export interface MedicationRemovedEvent {
  item: MedicationDisplayItem;
  index: number;
}

@Component({
  selector: 'xm-medication-widget-view',
  templateUrl: './medication-widget-view.component.html',
  styleUrls: ['./medication-widget-view.component.scss'],
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    XmedicalUiModule,
    PipesModule,
    MedicationListViewComponent,
    MedicineLibraryProductResultComponent
  ]
})
export class MedicationWidgetViewComponent {
  @ViewChild(AppMatAutocompleteComponent) autocomplete!: AppMatAutocompleteComponent;

  // Inputs
  items = input<MedicationDisplayItem[]>([]);
  title = input<string>('Medication');
  // Results as picker entries: practice items, plus medicine library products when the practice has the
  // library on (#333). A v1 caller maps its Medication[] with practiceSearchEntries.
  searchFn = input.required<(query: string) => Observable<MedicationSearchEntry[]>>();
  isProcessing = input<boolean>(false);
  canAdd = input<boolean>(true);
  canDelete = input<boolean>(true);
  addItemText = input<string>('Add new medication');
  usePrimaryText = input<boolean>(false);

  // Outputs
  itemAdded = output<MedicationAddedEvent>();
  itemRemoved = output<MedicationRemovedEvent>();
  addNewItem = output<string>();
  // A library product was picked. The host opens the add card for it and saves it to the practice list with
  // the product link, like "add new" with the name filled in.
  libraryProductSelected = output<MedicineLibraryProductMatch>();

  // Track comment for search results before adding
  resultComments: { [key: number]: string } = {};

  getResultData(result: Medication): { comment: string } {
    if (!(result.id in this.resultComments)) {
      this.resultComments[result.id] = result.comment || '';
    }
    return { comment: this.resultComments[result.id] };
  }

  setResultComment(itemId: number, value: string): void {
    this.resultComments[itemId] = value;
  }

  onItemSelected(result: Medication): void {
    const comment = result.id in this.resultComments
      ? this.resultComments[result.id]
      : (result.comment || '');
    this.itemAdded.emit({
      item: result,
      dosage: result.dosage || '',
      diagnosisCode: result.diagnosisCode || '',
      comment
    });
    this.clearSearch();
  }

  onLibraryProductSelected(product: MedicineLibraryProductMatch): void {
    this.clearSearch();
    this.libraryProductSelected.emit(product);
  }

  onRemoveItem(item: MedicationDisplayItem, index: number): void {
    this.itemRemoved.emit({ item, index });
  }

  onAddNewItem(): void {
    const searchTerm = this.autocomplete?.queryText || '';
    this.clearSearch();  // Close search panel
    this.addNewItem.emit(searchTerm);
  }

  private clearSearch(): void {
    this.resultComments = {};
    if (this.autocomplete) {
      this.autocomplete.cancelSearch();
    }
  }

}
